import { memo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { type LLMOutputComponent } from '@llm-ui/react';
import remarkGfm from 'remark-gfm';

const remarkPlugins = [remarkGfm];

const components: Partial<Components> = {
  h1: ({ ...props }) => (
    <h1 className="text-3xl font-bold mt-6 mb-3" {...props} />
  ),
  h2: ({ ...props }) => (
    <h2 className="text-2xl font-bold mt-5 mb-2" {...props} />
  ),
  h3: ({ ...props }) => (
    <h3 className="text-xl font-bold mt-4 mb-2" {...props} />
  ),
  h4: ({ ...props }) => (
    <h4 className="text-lg font-bold mt-3 mb-1" {...props} />
  ),
  h5: ({ ...props }) => (
    <h5 className="text-base font-bold mt-2 mb-1" {...props} />
  ),
  h6: ({ ...props }) => (
    <h6 className="text-sm font-bold mt-2 mb-1" {...props} />
  ),
  p: ({ ...props }) => <p className="mb-4 leading-7" {...props} />,
  a: ({ ...props }) => (
    <a
      className="text-blue-600 hover:underline"
      {...props}
      target="_blank"
      rel="noopener noreferrer"
    />
  ),
  ul: ({ ...props }) => <ul className="list-disc pl-6 mb-4" {...props} />,
  ol: ({ ...props }) => <ol className="list-decimal pl-6 mb-4" {...props} />,
  li: ({ ...props }) => <li className="mb-1" {...props} />,
  blockquote: ({ ...props }) => (
    <blockquote
      className="border-l-4 border-gray-200 pl-4 py-2 mb-4 italic"
      {...props}
    />
  ),
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    if (match) {
      return (
        <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto mb-4">
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      );
    }

    return (
      <code
        className="bg-gray-100 px-1.5 py-0.5 rounded-sm font-mono text-sm"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ ...props }) => (
    <pre
      className="bg-gray-100 p-4 rounded-md overflow-x-auto mb-4"
      {...props}
    />
  ),
  table: ({ ...props }) => (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full border-collapse" {...props} />
    </div>
  ),
  thead: ({ ...props }) => <thead className="bg-gray-50" {...props} />,
  tbody: ({ ...props }) => (
    <tbody className="divide-y divide-gray-200" {...props} />
  ),
  tr: ({ ...props }) => <tr className="border-b" {...props} />,
  th: ({ ...props }) => (
    <th className="px-4 py-2 text-left font-medium text-gray-900" {...props} />
  ),
  td: ({ ...props }) => <td className="px-4 py-2" {...props} />,
  hr: ({ ...props }) => (
    <hr className="my-6 border-t border-gray-200" {...props} />
  ),
  img: ({ alt, ...props }) => (
    <img
      className="max-w-full h-auto rounded-md my-4"
      alt={alt || ''}
      {...props}
    />
  ),
};

const NonMemoizedReactMarkdown = ({ children }: { children: string }) => {
  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
      {children}
    </ReactMarkdown>
  );
};

const MemoizedReactMarkdown = memo(
  NonMemoizedReactMarkdown,
  (prevProps, nextProps) => prevProps.children === nextProps.children
);

export const MarkdownComponent: LLMOutputComponent = ({ blockMatch }) => {
  const markdown = blockMatch.output;

  // Skip rendering if markdown is empty
  if (!markdown || markdown.trim() === '') {
    return null;
  }

  return <MemoizedReactMarkdown>{markdown}</MemoizedReactMarkdown>;
};

export default MarkdownComponent;
